import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Store, Briefcase, Palette, Camera, Shirt, Globe, Users, ShieldCheck,
  CreditCard, Mail, ArrowRight, CheckCircle2, Package,
} from 'lucide-react';
import { ENLogo } from '@/components/ENLogo';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const SERVICES = [
  { icon: Palette,  label: 'Graphic Design' },
  { icon: Globe,    label: 'Website Design & Development' },
  { icon: Camera,   label: 'Videography & Photography' },
  { icon: Briefcase,label: 'Marketing & Branding' },
  { icon: Shirt,    label: 'Fashion Show Production' },
  { icon: Package,  label: 'Merchandise Printing & Fulfilment' },
  { icon: Users,    label: 'Workshops & Consulting' },
  { icon: Store,    label: 'Other Creative Business Services' },
];

const PRODUCTS = [
  'Clothing & Apparel',
  'Printed Merchandise',
  'Original Artwork',
  'Accessories',
  'Handcrafted Indigenous Products',
];

function Card({ children, className = '' }) {
  return (
    <div className={`bg-[#13153A]/80 backdrop-blur-md border border-purple-500/20 rounded-2xl p-6 sm:p-8 ${className}`}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B] text-white" data-testid="about-page">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-8 sm:hidden"><ENLogo size="md" linkTo="/" /></div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-fuchsia-300 text-[11px] uppercase tracking-[0.18em] font-bold mb-3">About</p>
          <h1 className="font-syne text-3xl sm:text-5xl font-black tracking-tight mb-6" data-testid="about-title">
            An Indigenous-owned marketplace for real businesses, real services, real products.
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-3xl">
            <strong className="text-white">EntrepreNatives</strong> is an Indigenous-owned online marketplace and creative
            services platform. We connect Indigenous entrepreneurs, artists, designers, photographers,
            videographers, and small businesses with customers and other businesses who want to hire them or
            buy what they make.
          </p>
          <p className="text-zinc-400 text-sm sm:text-base mt-4 max-w-3xl">
            We are a <strong className="text-white">marketplace facilitator</strong> — vendors run their own
            independent businesses, set their own prices, deliver their own work, and receive their own payouts
            directly through Stripe. EntrepreNatives provides the technology, payment processing,
            discoverability, and dispute resolution that lets them sell with confidence.
          </p>
        </motion.div>

        {/* Marketplace explainer */}
        <Card className="mt-10" data-testid="about-marketplace-explainer">
          <h2 className="font-syne text-xl sm:text-2xl font-bold mb-4">How the marketplace works</h2>
          <ol className="space-y-3 text-zinc-300 text-sm sm:text-base">
            <li className="flex gap-3"><span className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-200 font-bold text-xs">1</span><span><strong className="text-white">Vendors create a verified profile</strong> showcasing their services or products, with photos, pricing, descriptions, and business info.</span></li>
            <li className="flex gap-3"><span className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-200 font-bold text-xs">2</span><span><strong className="text-white">Customers browse and book or buy</strong> directly through the platform. Some services are fixed-price, others allow custom quotes for larger projects.</span></li>
            <li className="flex gap-3"><span className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-200 font-bold text-xs">3</span><span><strong className="text-white">Payments are processed by Stripe.</strong> Customers pay through Stripe Checkout. Vendors receive their own payouts via Stripe Connect — EntrepreNatives is not a financial institution and does not hold customer funds.</span></li>
            <li className="flex gap-3"><span className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-200 font-bold text-xs">4</span><span><strong className="text-white">Vendors deliver the work.</strong> Physical products are fulfilled and shipped directly by the vendor. Digital and creative services are delivered electronically or in person depending on the project type.</span></li>
            <li className="flex gap-3"><span className="shrink-0 w-7 h-7 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-200 font-bold text-xs">5</span><span><strong className="text-white">The platform monitors activity</strong> to prevent fraud, prohibited goods, or misuse, and provides dispute resolution between buyers and vendors.</span></li>
          </ol>
        </Card>

        {/* What we sell */}
        <Card className="mt-6" data-testid="about-what-we-sell">
          <h2 className="font-syne text-xl sm:text-2xl font-bold mb-4">What is sold through EntrepreNatives</h2>
          <p className="text-zinc-400 text-sm mb-6">All transactions are for lawful business services and physical products only. We do not sell restricted, prohibited, or high-risk products.</p>

          <h3 className="text-fuchsia-300 text-xs uppercase tracking-[0.18em] font-bold mb-3">Services offered by vendors</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            {SERVICES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-3 py-3">
                <Icon size={18} className="text-fuchsia-300 shrink-0" />
                <span className="text-zinc-200 text-sm font-semibold">{label}</span>
              </div>
            ))}
          </div>

          <h3 className="text-fuchsia-300 text-xs uppercase tracking-[0.18em] font-bold mb-3">Physical products sold by vendors</h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {PRODUCTS.map((p) => (
              <span key={p} className="text-zinc-200 text-sm bg-white/[0.04] border border-white/10 rounded-full px-3.5 py-1.5">{p}</span>
            ))}
          </div>

          <p className="text-zinc-400 text-sm">
            The platform also offers <strong className="text-white">Website Builder subscriptions</strong> and
            <strong className="text-white"> booking / payment processing</strong> for vendors providing
            legitimate creative business services.
          </p>
        </Card>

        {/* Who we serve */}
        <Card className="mt-6" data-testid="about-audience">
          <h2 className="font-syne text-xl sm:text-2xl font-bold mb-4">Who EntrepreNatives serves</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
              <p className="text-fuchsia-300 text-[11px] uppercase tracking-[0.18em] font-bold mb-2">Individual end customers</p>
              <p className="text-zinc-200 text-sm">Consumers who hire Indigenous creatives, photographers, designers, fashion vendors, and artists, or buy clothing, art, accessories, and merchandise directly from them.</p>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5">
              <p className="text-fuchsia-300 text-[11px] uppercase tracking-[0.18em] font-bold mb-2">Other businesses (B2B)</p>
              <p className="text-zinc-200 text-sm">Companies, non-profits, and other vendors hiring Indigenous creatives for branding, websites, print runs, photography, video, marketing, and consulting services.</p>
            </div>
          </div>
        </Card>

        {/* Trust + transparency */}
        <Card className="mt-6" data-testid="about-trust">
          <h2 className="font-syne text-xl sm:text-2xl font-bold mb-4">Trust, safety & transparency</h2>
          <ul className="space-y-2.5 text-zinc-300 text-sm">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" /><span>Vendor identity is verified through <strong className="text-white">Stripe Connect</strong> KYC before any payouts can be released.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" /><span>All card payments are processed by <strong className="text-white">Stripe</strong> (PCI DSS Level 1). EntrepreNatives never stores card numbers.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" /><span>Vendors receive their own payouts directly to their Stripe-connected bank accounts.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" /><span>Acceptable Use Policy prohibits illegal, restricted, or high-risk goods and services.</span></li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-300 shrink-0 mt-0.5" /><span>Dispute resolution between buyers and vendors is handled through the platform.</span></li>
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/trust-safety" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Trust & Safety</Link>
            <Link to="/legal/refund-policy" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Refund Policy</Link>
            <Link to="/terms" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Privacy Policy</Link>
            <Link to="/legal/acceptable-use" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 hover:text-white">Acceptable Use</Link>
          </div>
        </Card>

        {/* CTAs */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <Link to="/browse" data-testid="about-cta-browse" className="group bg-gradient-to-r from-fuchsia-500 to-violet-600 hover:from-fuchsia-400 hover:to-violet-500 transition-all rounded-2xl p-6 text-white font-bold flex items-center justify-between">
            <span>Browse real vendors & services</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/contact" data-testid="about-cta-contact" className="group bg-white/5 hover:bg-white/10 border border-white/10 transition-all rounded-2xl p-6 text-white font-bold flex items-center justify-between">
            <span className="flex items-center gap-2"><Mail size={18} /> Contact our team</span>
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <p className="text-zinc-500 text-xs mt-12 leading-relaxed max-w-3xl">
          EntrepreNatives is a marketplace platform connecting customers with independent Indigenous-owned
          businesses and service providers. EntrepreNatives is not a party to transactions between vendors and
          buyers, is not a money transmitter, and is not a financial institution. All payment processing is
          handled by Stripe, a licensed payment processor.
        </p>
      </div>
      <Footer />
    </div>
  );
}
